import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, map } from 'rxjs';

import { PaginationMetadata } from '@core/shared/domain';
import { NotificationService } from '@core/shared/util';
import {
  AssignedIndustryCatalogFilterQueryModel,
  AssignedIndustryCatalogSearchQueryField,
  IndustryCatalogAssignment,
  MarkPublishFilter,
  MarkPublishResponse,
  PlatformCatalogFilter,
  PlatformIndustryCatalogFilter,
  PlatformcatalogSearchQueryField,
  PlattformkatalogeService,
} from '@mp/content-manager/plattformkataloge/data-access';
import { QueryParamsStoreFilterFacade } from '@mp/shared/data-access';

@Injectable()
export class IndustriekatalogeFacade {
  private readonly _pagination$ = new BehaviorSubject(undefined as undefined | PaginationMetadata);
  readonly pagination$ = this._pagination$.asObservable();

  private readonly _isLoading$ = new BehaviorSubject(false as boolean);
  readonly isLoading$ = this._isLoading$.asObservable();

  private readonly _kataloge$ = new BehaviorSubject([] as Array<IndustryCatalogAssignment>);
  readonly kataloge$ = this._kataloge$.asObservable();

  private readonly _katalogPublished$ = new Subject<MarkPublishResponse & { plattformkatalogId: string }>();
  readonly katalogPublished$ = this._katalogPublished$.asObservable();

  get alreadyAttachedKataloge(): Array<IndustryCatalogAssignment> {
    return this._kataloge$.getValue();
  }

  get industriekatalogFilter(): PlatformIndustryCatalogFilter {
    return {
      ...this.plattformkatalogFilterFacade.filter,
    };
  }

  public readonly hasActiveIndustryCatalogFilter$ = this.plattformkatalogFilterFacade.filter$.pipe(
    map(({ catalogType, updatedOnly, searchTerm, field }) => {
      if ((catalogType?.length ?? 0) > 0 || updatedOnly === true) {
        return true;
      }

      if (searchTerm) {
        const searchField = this.getIndustryCatalogSearchField(field);
        return searchField != null || field == null;
      }

      return false;
    }),
  );

  constructor(
    private readonly service: PlattformkatalogeService,
    private readonly plattformkatalogFilterFacade: QueryParamsStoreFilterFacade<PlatformCatalogFilter>,
    private readonly toaster: NotificationService,
  ) {}

  /**
   * Removes an Industriekatalog from an existing Plattformkatalog.
   * Note: Also fetches the new list of Industriekataloges after the update.
   * @param id the id of the Plattformkatalog
   * @param katalogId the id of the Industriekatalog to remove
   * @param remove Indicates wether the association should be removed. `false` to undo the removal.
   */
  removeIndustriekatalogFromPlattformkatalog(id: string, katalogId: string, remove = true): void {
    this.service.removeIndustriekatalogeFromPlattformkatalog(id, katalogId, remove).subscribe({
      next: () => {
        // Change catalog property to place. TODO: EntityStore would be better here
        const catalogs = this._kataloge$.getValue();

        const updatedCatalogs = catalogs.map((catalog) =>
          catalog.catalogId === katalogId ? { ...catalog, isRemoved: remove } : catalog,
        );

        this._kataloge$.next(updatedCatalogs);
      },
    });
  }

  /**
   * Removes an Industriekatalog from an existing Plattformkatalog.
   * Note: Also fetches the new list of Industriekataloges after the update.
   * @param id the id of the Plattformkatalog
   * @param katalogIds the ids of the Industriekataloge to add
   */
  addIndustriekatalogeToPlattformkatalog(
    id: string,
    katalogIds: Array<string>,
    filterOptions: PlatformIndustryCatalogFilter = this.industriekatalogFilter,
  ): void {
    this.service.addIndustriekatalogeToPlattformkatalog(id, katalogIds).subscribe({
      next: () => {
        this.promptSuccessToast(katalogIds);
        this.getIndustriekatalogeForPlattformkatalog(id, filterOptions, { noLoading: true });
      },
    });
  }

  private promptSuccessToast(katalogIds: Array<unknown>): void {
    const multipleAdded = katalogIds.length > 1;
    const message = multipleAdded
      ? `${katalogIds.length} Industriekataloge erfolgreich hinzugefügt!`
      : 'Industriekatalog erfolgreich hinzugefügt!';

    this.toaster.toastSuccess(message);
  }

  /**
   * Re-sorts an Industriekatalog of an existing Plattformkatalog.
   * Note: Also fetches the new list of Industriekataloges after the update.
   * @param id the id of the Plattformkatalog
   * @param sorting the id, and position(s) of the Industriekataloge to sort
   */
  sortIndustrieKatalogOfPlattformkatalog(
    id: string,
    sorting: { id: string; previousIndex: number; currentIndex: number },
    filterOptions: PlatformIndustryCatalogFilter = this.industriekatalogFilter,
  ): void {
    // TODO: Review this!
    // We proactively sort the Kataloge, so that the user can see the result immediately
    // NOTE: Please note that this causes inconsistencies if the request fails!
    const calculatedStartIndex = (filterOptions.page - 1) * filterOptions.pageSize;
    this.optimisticallySortClientSide(sorting, calculatedStartIndex);

    this.service
      .sortIndustriekatalogeForPlattformkatalog(id, { id: sorting.id, newIndex: sorting.currentIndex })
      .subscribe({
        next: () => this.getIndustriekatalogeForPlattformkatalog(id, filterOptions, { noLoading: true }),
      });
  }

  private optimisticallySortClientSide(
    { previousIndex, currentIndex }: { previousIndex: number; currentIndex: number },
    calculatedStartIndex: number,
  ): void {
    const currentKataloge = this._kataloge$.getValue();
    moveItemInArray(currentKataloge, previousIndex - calculatedStartIndex, currentIndex - calculatedStartIndex);
    currentKataloge.forEach((katalog, index) => (katalog.order = index + calculatedStartIndex + 1));
    this._kataloge$.next(currentKataloge);
  }

  /**
   * Gets a page of Industriekataloge of an existing Plattformkatalog.
   *
   * If no {@link filterOptions} or {@link pagination} parameter given, the current values will be read from the filter facade.
   * @param id the id of the Plattformkatalog
   * @param filterOptions the filter options to use for the fetch
   * @param pagination the pagination to use for the fetch
   * @param params additional parameter that allows to prevent the loading indicator from being triggered
   */
  getIndustriekatalogeForPlattformkatalog(
    id: string,
    filterOptions: PlatformIndustryCatalogFilter = this.industriekatalogFilter,
    { noLoading }: { noLoading: boolean } = { noLoading: false },
  ): void {
    if (!noLoading) {
      this._isLoading$.next(true);
    }

    const { field, searchTerm, updatedOnly, ...rest } = this.plattformkatalogFilterFacade.filter;

    const industrySearchField = this.getIndustryCatalogSearchField(field);

    const filter: AssignedIndustryCatalogFilterQueryModel = {
      ...rest,
      isUpdated: updatedOnly,
      // Only apply search term if no field or an industry catalog relevant field is specified
      searchTerm: industrySearchField != null || field == null ? searchTerm : undefined,
      field: industrySearchField,
      ...filterOptions,
    };

    this.service.getIndustriekatalogeForPlattformkatalog(id, filter).subscribe({
      next: ({ data, pagination }) => {
        this._kataloge$.next(data);
        this._pagination$.next(pagination);

        if (!noLoading) {
          this._isLoading$.next(false);
        }
      },
    });
  }

  markKatalogeForPublishing(
    plattformkatalogId: string,
    publish: boolean,
    industrieKatalogIds?: Array<string>,
    filter?: MarkPublishFilter,
  ): void {
    this.service
      .markIndustriekatalogeForPublishing(plattformkatalogId, publish, industrieKatalogIds, filter)
      .subscribe({
        next: (publishResult: MarkPublishResponse) => {
          this._katalogPublished$.next({ ...publishResult, plattformkatalogId });

          this.getIndustriekatalogeForPlattformkatalog(plattformkatalogId, this.industriekatalogFilter, {
            noLoading: true,
          });
        },
      });
  }

  private getIndustryCatalogSearchField(
    globalSearchField: PlatformcatalogSearchQueryField | undefined,
  ): AssignedIndustryCatalogSearchQueryField | undefined {
    switch (globalSearchField) {
      case PlatformcatalogSearchQueryField.All:
        return AssignedIndustryCatalogSearchQueryField.All;
      case PlatformcatalogSearchQueryField.IndustryCatalogName:
        return AssignedIndustryCatalogSearchQueryField.IndustryCatalogName;
      case PlatformcatalogSearchQueryField.SupplierName:
        return AssignedIndustryCatalogSearchQueryField.SupplierName;
      default:
        return undefined;
    }
  }
}
